<template>
  <m-field
    :data-testid="testId"
    class="field-wrapper"
    :status="hasError ? 'danger' : 'default'"
    :class="`combobox-${$.uid}`">
    <m-field-label slot="label" v-if="label">
      {{ label }}

      <help-tooltip v-if="hint" :text="hint" />
    </m-field-label>
    <vuetify-combobox
      density="compact"
      class="multi-chip-combobox"
      :attach="`.combobox-${$.uid}`"
      :items="options"
      v-bind="{ ...$props }"
      hide-details="hide"
      :label="null"
      variant="outlined"
      :return-object="false"
      :style="computedWidth"
      persistent-placeholder
      item-title="label"
      item-value="value"
      :placeholder="
        placeholder ?? $t('forms.elements.selects.combobox.placeholders.selectOrEnterAValue')
      "
      :menu-props="{ bottom: true, offsetY: true, rounded: true, ripple: false }"
      :model-value="modelValue"
      @update:model-value="val => (internalValue = val)">
      <template #item="{ item, props }">
        <v-list-item
          v-bind="props"
          :class="{ selected: isSelected(item.raw) }"
          :data-testid="`${testId}-menu-item`"
          class="px-5">
          {{ item.label }}
          <basic-icon v-if="isSelected(item.raw)">check</basic-icon>
        </v-list-item>
      </template>
    </vuetify-combobox>
    <m-field-hint :data-testid="`${testId}-errors`" v-if="shouldDisplayMessages">
      {{ errorBucket.join(', ') }}
    </m-field-hint>
  </m-field>
</template>

<script>
import { isEqual, some } from 'lodash';
import fieldMixin from '@/components/mixins/fieldMixin';
import validatable from '@/components/mixins/validatable';
import testable from '@/components/mixins/testable';
import { BasicIcon, HelpTooltip } from '@/components';
import { VCombobox as VuetifyCombobox } from 'vuetify/lib/components/VCombobox/VCombobox.mjs';
import { removeEventsFromAttrs, validateV2DropDownOptionsProp } from '@satellite/plugins/util';

/**
 * ComboBox wraps Vuetify V2 library's <v-combobox>
 * @displayName Combo Box
 */
export default {
  name: 'ComboBox',
  mixins: [fieldMixin, validatable, testable],
  components: { VuetifyCombobox, BasicIcon, HelpTooltip },
  props: {
    /**
     * null type to allow "any"
     * @model
     */
    modelValue: {
      type: null,
      required: false
    },
    /**
     * Field options
     * [ { label: 'option label', value: 'option value' } ]
     */
    options: {
      type: Array,
      required: false,
      default() {
        return [];
      },
      validator(value) {
        return validateV2DropDownOptionsProp(value, 'MultiDropDownField');
      }
    },
    /**
     * Placement of the open drop down box
     */
    placement: {
      type: String,
      required: false,
      default: 'bottom-start'
    },
    /**
     * Position of the open drop down box
     */
    position: {
      type: String,
      required: false,
      default: 'absolute'
    }
  },
  methods: {
    removeEventsFromAttrs,
    isSelected(option) {
      return some(this.modelValue, val => isEqual(option?.value ?? option, val));
    }
  }
};
</script>

<style scoped lang="scss">
:deep(.v-list-item__content) {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.v-input {
  margin-top: 0 !important;
}

.field-wrapper {
  flex: 1;
  position: relative;
}
.dropdown-menu {
  max-height: 10rem;
  width: 100%;
}
.select-option {
  justify-content: space-between;
}
.selected {
  background-color: $m-color-background-highlight;
  font-weight: $m-font-weight-bold;
}

m-dropdown-item {
  width: 100%;
  justify-content: space-between;
}

:deep(.v-input) {
  padding: 0;
  margin: 0;
}

:deep(.v-field__field) {
  padding: 0 10px;
}

:deep(.v-field__input) {
  min-height: 40px !important;
  max-height: 40px !important;
  transition:
    color 0.35s ease-in-out 0s,
    background-color 0.35s ease-in-out 0s,
    border-color 0.35s ease-in-out 0s;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  font-family: var(--m-font-family-default, 'Manrope', sans-serif);
  line-height: var(--m-line-height-5, 150%);
  font-size: var(--m-font-size-3, 0.875rem);
  font-weight: var(--m-font-weight-regular, 500);
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  block-size: var(--m-dropdown-trigger-height);
  inline-size: 100%;
  box-sizing: border-box;
  text-decoration: none;
  gap: var(--m-spacing-1, 4px);
  padding-inline-end: var(--m-dropdown-trigger-padding-x);
  padding-inline-start: var(--m-dropdown-trigger-padding-x);
  padding-block-start: var(--m-dropdown-trigger-padding-y);
  padding-block-end: var(--m-dropdown-trigger-padding-y);
  background-color: var(--m-dropdown-trigger-background-color);
  color: var(--m-dropdown-trigger-color);
  border: 1px solid var(--m-dropdown-trigger-border-color);
  border-radius: var(--m-dropdown-trigger-border-radius);
  outline: none;
}

:deep(.v-list-item) {
  padding: 0 $m-spacing-2;
  min-height: auto;
  &.v-list-item--highlighted {
    background-color: $m-color-background-primary;
  }
}

:deep(.v-list-item__content) {
  padding: 0;
}

:deep(.v-list-item--link:before) {
  display: none;
}

:deep(.v-list-item--active .v-list-item__title) {
  background: $m-color-background-tertiary;
}

:deep(.v-list-item__title) {
  padding: 0 $m-spacing-1 !important;
  --m-dropdown-item-background-color: rgba(var(--m-color-background-primary, 252, 252, 255), 1);
  --m-dropdown-item-color: rgba(var(--m-color-text-secondary, 61, 74, 85), 1);
  transition:
    color 0.35s ease-in-out 0s,
    background-color 0.35s ease-in-out 0s;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  font-family: var(--m-font-family-default, 'Manrope', sans-serif);
  line-height: var(--m-line-height-5, 150%);
  font-size: var(--m-font-size-3, 0.875rem);
  font-weight: var(--m-font-weight-regular, 500);
  block-size: 36px;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  gap: var(--m-spacing-2, 8px);
  padding-inline-end: var(--m-spacing-1, 4px);
  padding-inline-start: var(--m-spacing-1, 4px);
  padding-block-start: var(--m-spacing-2, 8px);
  padding-block-end: var(--m-spacing-2, 8px);
  background-color: var(--m-dropdown-item-background-color);
  color: var(--m-dropdown-item-color);
  border: none;
  border-radius: var(--m-border-radius-s, 4px);
}

:deep(.v-ripple__container) {
  display: none !important;
}

:deep(.v-list-item__action) {
  display: none;
}

:deep(.v-menu__content) {
  transform: translateY(25px);
}
</style>
